import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, ConfigProvider, Form, Input, Spin, Typography } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { Formik } from "formik";
import * as yup from "yup";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import APIService from "../../Service/APIService";

const { Title, Text } = Typography;

const PASSWORD_RULES = [
  { key: "length", label: "At least 12 characters", test: (value) => value.length >= 12 },
  { key: "max", label: "No more than 64 characters", test: (value) => value.length <= 64 },
  { key: "lower", label: "One lowercase letter", test: (value) => /[a-z]/.test(value) },
  { key: "upper", label: "One uppercase letter", test: (value) => /[A-Z]/.test(value) },
  { key: "number", label: "One number", test: (value) => /[0-9]/.test(value) },
  { key: "special", label: "One special character", test: (value) => /[^A-Za-z0-9]/.test(value) },
];

const passwordSchema = yup.object().shape({
  password: yup
    .string()
    .required("Password is required")
    .min(12, "Password must be at least 12 characters")
    .max(64, "Password must be no longer than 64 characters")
    .matches(/[a-z]/, "Password must include at least one lowercase letter")
    .matches(/[A-Z]/, "Password must include at least one uppercase letter")
    .matches(/[0-9]/, "Password must include at least one number")
    .matches(/[^A-Za-z0-9]/, "Password must include at least one special character"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required("Please confirm your password"),
});

const PasswordActionForm = ({ mode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const apiService = useMemo(() => new APIService(), []);
  const [tokenState, setTokenState] = useState({
    loading: true,
    valid: false,
    reason: "",
    email: "",
    purpose: "",
  });

  const queryParams = new URLSearchParams(location.search);
  const token = String(queryParams.get("token") || "").trim();
  const isSetupMode = mode === "setup";
  const expectedPurpose = isSetupMode ? "set_password" : "reset_password";

  useEffect(() => {
    let mounted = true;

    const validateToken = async () => {
      if (!token) {
        if (mounted) {
          setTokenState({
            loading: false,
            valid: false,
            reason: "The link is missing a token.",
            email: "",
            purpose: "",
          });
        }
        return;
      }

      try {
        const response = await apiService.getDataFromBackend(
          `auth/action/validate?token=${encodeURIComponent(token)}`
        );
        const data = response?.data || {};
        if (!mounted) return;

        if (!data.valid || data.purpose !== expectedPurpose) {
          setTokenState({
            loading: false,
            valid: false,
            reason: "The link is invalid or no longer valid for this action.",
            email: data?.email || "",
            purpose: data?.purpose || "",
          });
          return;
        }

        setTokenState({
          loading: false,
          valid: true,
          reason: "",
          email: data?.email || "",
          purpose: data?.purpose || "",
        });
      } catch (error) {
        const reason =
          error?.response?.data?.reason === "expired"
            ? "This link has expired. Request a new one and try again."
            : "This link is invalid or has already been used.";
        if (mounted) {
          setTokenState({
            loading: false,
            valid: false,
            reason,
            email: "",
            purpose: "",
          });
        }
      }
    };

    validateToken();
    return () => {
      mounted = false;
    };
  }, [apiService, expectedPurpose, token]);

  const submitHandler = async (values, { setSubmitting }) => {
    try {
      const response = await apiService.addDataToBackend(
        {
          Password: values.password,
          ConfirmPassword: values.confirmPassword,
        },
        `${isSetupMode ? "setpassword" : "resetpassword"}?token=${encodeURIComponent(token)}`
      );

      if (response?.status === 200 || response?.status === 201) {
        await Swal.fire({
          title: "Success",
          text: isSetupMode
            ? "Your password has been set successfully. You can log in now."
            : "Your password has been reset successfully. You can log in now.",
          icon: "success",
          confirmButtonColor: "#1677ff",
        });
        navigate("/Login");
        return;
      }

      throw new Error("Unexpected response");
    } catch (error) {
      const errorData = error?.response?.data || {};
      const messageLines = Array.isArray(errorData.errors) && errorData.errors.length > 0
        ? errorData.errors
        : [errorData.message || "The link may be invalid or expired."];

      Swal.fire({
        title: "Unable to continue",
        html: messageLines.map((line) => `<div>${line}</div>`).join(""),
        icon: "error",
        confirmButtonColor: "#1677ff",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const ruleStates = (password) =>
    PASSWORD_RULES.map((rule) => ({
      ...rule,
      passed: rule.test(password || ""),
    }));

  const pageTitle = isSetupMode ? "Set Up Your Password" : "Reset Password";
  const pageSubtitle = isSetupMode
    ? "Create a secure password to finish activating your Tissue Vault account."
    : "Choose a new secure password for your Tissue Vault account.";
  const buttonLabel = isSetupMode ? "Set Password" : "Reset Password";

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "'Figtree', sans-serif",
          borderRadius: 10,
          colorPrimary: "#1677ff",
        },
      }}
    >
      <div style={backgroundWrapper}>
        <div style={containerStyle}>
          <div style={{ marginBottom: "32px" }}>
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              style={{ width: "180px" }}
              alt="Tissue Vault Logo"
            />
          </div>

          <Card bordered={false} style={cardStyle}>
            {tokenState.loading ? (
              <div style={loadingContainerStyle}>
                <Spin size="large" />
                <Text type="secondary">Validating your secure link...</Text>
              </div>
            ) : !tokenState.valid ? (
              <>
                <div style={{ marginBottom: "24px" }}>
                  <Title level={2} style={{ margin: 0, fontWeight: 500 }}>
                    Link Unavailable
                  </Title>
                  <Text type="secondary" style={{ fontSize: "15px" }}>
                    {tokenState.reason}
                  </Text>
                </div>
                <Alert
                  type="error"
                  showIcon
                  message="Request a new link"
                  description="Use the Forgot Password page or ask an administrator to resend your invite."
                  style={{ marginBottom: "20px" }}
                />
                <div style={{ textAlign: "center" }}>
                  <Link to="/Forgot" style={backToLoginLink}>
                    Request a New Link
                  </Link>
                </div>
              </>
            ) : (
              <Formik
                initialValues={{ password: "", confirmPassword: "" }}
                validationSchema={passwordSchema}
                onSubmit={submitHandler}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  isValid,
                  dirty,
                }) => (
                  <>
                    <div style={{ marginBottom: "28px" }}>
                      <Title level={2} style={{ margin: 0, fontWeight: 500 }}>
                        {pageTitle}
                      </Title>
                      <Text type="secondary" style={{ fontSize: "15px" }}>
                        {pageSubtitle}
                      </Text>
                      {tokenState.email ? (
                        <div style={{ marginTop: "12px" }}>
                          <Text style={{ fontSize: "14px", color: "#0f172a", fontWeight: 600 }}>
                            Account: {tokenState.email}
                          </Text>
                        </div>
                      ) : null}
                    </div>

                    <Form layout="vertical" onFinish={handleSubmit} requiredMark={false}>
                      <Form.Item
                        label={<span style={labelStyle}>New Password</span>}
                        validateStatus={touched.password && errors.password ? "error" : ""}
                        help={touched.password && errors.password}
                      >
                        <Input.Password
                          name="password"
                          prefix={<LockOutlined style={{ color: "#bfbfbf", marginRight: "4px" }} />}
                          placeholder="Enter new password"
                          size="large"
                          style={inputStyle}
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Form.Item>

                      <Form.Item
                        label={<span style={labelStyle}>Confirm Password</span>}
                        validateStatus={touched.confirmPassword && errors.confirmPassword ? "error" : ""}
                        help={touched.confirmPassword && errors.confirmPassword}
                      >
                        <Input.Password
                          name="confirmPassword"
                          prefix={<CheckCircleOutlined style={{ color: "#bfbfbf", marginRight: "4px" }} />}
                          placeholder="Confirm new password"
                          size="large"
                          style={inputStyle}
                          value={values.confirmPassword}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Form.Item>

                      <div style={checklistStyle}>
                        {ruleStates(values.password).map((rule) => (
                          <div key={rule.key} style={checklistItemStyle}>
                            {rule.passed ? (
                              <CheckCircleOutlined style={{ color: "#16a34a" }} />
                            ) : (
                              <CloseCircleOutlined style={{ color: "#94a3b8" }} />
                            )}
                            <span style={{ color: rule.passed ? "#166534" : "#475569" }}>{rule.label}</span>
                          </div>
                        ))}
                      </div>

                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={isSubmitting}
                        disabled={!tokenState.valid || !(isValid && dirty)}
                        style={buttonStyle}
                        block
                      >
                        {buttonLabel}
                      </Button>

                      <div style={{ textAlign: "center", marginTop: "16px" }}>
                        <Link to="/Login" style={backToLoginLink}>
                          Back to Log In
                        </Link>
                      </div>
                    </Form>
                  </>
                )}
              </Formik>
            )}
          </Card>

          <div style={footerLogoStyle}>
            <Text type="secondary" style={{ fontSize: "12px", marginRight: "8px" }}>
              POWERED BY
            </Text>
            <img
              src={`${process.env.PUBLIC_URL}/companyLogo.png`}
              style={{ width: "120px", filter: "opacity(0.6)" }}
              alt="Advanced Solution Logo"
            />
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

const backgroundWrapper = {
  background: "#EDF3FF",
  minHeight: "100vh",
  width: "100%",
};

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  padding: "24px",
};

const cardStyle = {
  width: "100%",
  maxWidth: "540px",
  borderRadius: "20px",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
  padding: "12px",
};

const labelStyle = {
  fontWeight: "600",
  fontSize: "14px",
  color: "#434343",
};

const inputStyle = {
  height: "48px",
  borderRadius: "10px",
};

const buttonStyle = {
  height: "48px",
  borderRadius: "10px",
  fontSize: "16px",
  fontWeight: "600",
  marginTop: "12px",
};

const checklistStyle = {
  display: "grid",
  gap: "8px",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  padding: "14px 16px",
  marginBottom: "8px",
};

const checklistItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  fontSize: "14px",
};

const loadingContainerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "16px",
  minHeight: "220px",
};

const backToLoginLink = {
  fontSize: "14px",
  fontWeight: "600",
  color: "#64748b",
};

const footerLogoStyle = {
  marginTop: "48px",
  display: "flex",
  alignItems: "center",
};

export default PasswordActionForm;
